'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { RefObject } from 'react';
import { VariantProps } from 'tailwind-variants';

import DayIcon from '@/fo/icons/base/diagnostic/day.svg';
import MorningIcon from '@/fo/icons/base/diagnostic/morning.svg';
import NightIcon from '@/fo/icons/base/diagnostic/night.svg';
import IconDelete from '@/fo/icons/base/icons/delete.svg';
import IconHeartActive from '@/fo/icons/base/icons/heart-active.svg';
import IconHeart from '@/fo/icons/base/icons/heart.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitLabelBase } from '@/fo/ui/kit/label/base';
import { UiKitLabelBrand } from '@/fo/ui/kit/label/brand';
import { UiKitPriceBase } from '@/fo/ui/kit/price/base';
import { UiLoyaltyInformerCount } from '@/fo/ui/loyalty/informer/count';
import { UiProductCounterInputBase } from '@/fo/ui/product/counter-input/base';
import { UiProductOptionsBase } from '@/fo/ui/product/options/base';
import { utilEventProductSubscribeModal } from '@/fo/util/event-product-subscribe-modal';
import { MappedProduct, Option } from '@/fo/util/map-product-data';

import { GroupItems } from '../components/GroupItems/GroupItems';
import { ProductInGroupTile } from '../components/ProductInGroupTile/ProductInGroupTile';

import { styles } from './UiProductTileSecondary.styles';

type Variants = VariantProps<typeof styles>;

export interface UiProductTileSecondaryProps {
  ref?: RefObject<HTMLDivElement | null>;
  type?: Variants['type'];
  className?: string;
  classNameActions?: string;
  /** Авторизован ли пользователь */
  isAuthorized?: boolean;
  /** Ссылка на страницу товара */
  link?: string;
  /** Открывать ссылку в новой вкладке */
  openInNewTab?: boolean;
  /** Изображение товара */
  image: string;
  /** Название бренда */
  brand?: {
    color: string;
    name: string;
    url: string;
  };
  /** Название товара */
  title: string;
  /** Название серии (гамма) */
  series?: string;
  /** Описание товара */
  description: string;
  /** Текущая цена */
  price: number;
  /** Старая цена */
  oldPrice?: number | null;
  /** Скидка */
  salePrice?: number;
  /** Количество баллов лояльности */
  bonuses?: number;
  bonusesMax?: number;
  /** Опции товара (объем, цвет и т.д.) */
  options?: Option[];
  /** Атрибут visibility товара */
  visibility?: string;
  /** Обработчик клика по карточке */
  onClick?: () => void;
  /** Обработчик добавления в корзину */
  onCartClick?: () => void;
  /** Обработчик изменения количества */
  onCartChange?: (count: number) => void;
  /** Обработчик клика по опции */
  onOptionClick?: (option: Option) => void;
  /** Обработчик клика по избранному */
  onWishlistClick?: () => void;
  /** Обработчик клика по удалению */
  onRemoveClick?: () => void;
  /** Текущее количество */
  count?: number;
  /** В избранном */
  isInWishlist?: boolean;
  /** Тип группы */
  groupType?: number | null;
  /** Дочерние товары */
  childrenItems?: MappedProduct[];
  /** Обработчик клика по избранному для дочерних товаров */
  onWishlistChildrenClick?: (
    sku: string,
    wishlistSku: string,
    parentSku?: string,
    wishlistItemId?: string,
    isInWishlist?: boolean
  ) => void;
  /** Ссылка на дочерний товар */
  childrenLink?: (sku: string) => string;
  /** Корзина */
  isCart?: boolean;
  /** Загрузка кнопки корзины */
  isCartLoading?: boolean;
  /** Mindbox ID */
  mindboxId?: string;
  /** Максимальное количество */
  maxCount?: number;
  /** Лейблы диагностики */
  diagnosticLabels?: string[];
  /** Показывать ли диагностические лейблы */
  isDiagnostic?: boolean;
}

export function UiProductTileSecondary({
  ref,
  type = 'base',
  className,
  classNameActions,
  isAuthorized,
  link,
  openInNewTab,
  image,
  brand,
  series,
  title,
  description,
  price,
  oldPrice,
  salePrice = 0,
  bonuses = 0,
  bonusesMax = 0,
  options,
  visibility,
  onClick,
  onCartClick,
  onCartChange,
  onOptionClick,
  onWishlistClick,
  onRemoveClick,
  count = 1,
  isInWishlist,
  childrenItems,
  groupType,
  onWishlistChildrenClick,
  isCart,
  isCartLoading,
  mindboxId,
  maxCount,
  diagnosticLabels,
  isDiagnostic = false,
  ...props
}: UiProductTileSecondaryProps) {
  const t = useTranslations();
  const slots = styles({ type });
  const diagnosticIconByLabel: Record<
    string,
    React.ComponentType<React.SVGProps<SVGSVGElement>>
  > = {
    morning: MorningIcon,
    day: DayIcon,
    night: NightIcon
  };

  const diagnosticIcons =
    isDiagnostic && diagnosticLabels
      ? diagnosticLabels
          .map((label, idx) => {
            const key = String(label || '')
              .toLowerCase()
              .trim();
            const Icon = diagnosticIconByLabel[key];
            return Icon ? (
              <Icon
                key={`${key}-${idx}`}
                aria-label={label}
                className="h-6 w-auto"
              />
            ) : null;
          })
          .filter(Boolean)
      : [];

  return (
    <div
      ref={ref}
      className={slots.tileWrapper()}
    >
      <div
        className={slots.base({ className })}
        {...props}
      >
        {link && (
          <Link
            className={slots.link()}
            href={link}
            onClick={onClick}
            target={openInNewTab ? '_blank' : undefined}
            rel={openInNewTab ? 'noopener noreferrer' : undefined}
          />
        )}

        <div className={slots.imageContainer()}>
          <Image
            className={slots.image()}
            src={image}
            alt={title}
            width={120}
            height={120}
          />
          {(type === 'gift' || type === 'sample') && (
            <UiKitLabelBase
              className={slots.giftLabel()}
              size="s"
              color="done"
            >
              {t('gift-title')}
            </UiKitLabelBase>
          )}
          {diagnosticIcons.length > 0 && (
            <div className={slots.diagnosticLabels()}>{diagnosticIcons}</div>
          )}
        </div>

        <div className={slots.content()}>
          <div className={slots.header()}>
            {brand && (
              <UiKitLabelBrand
                color={brand.color}
                url={brand.url}
                size="s"
              >
                {brand.name}
              </UiKitLabelBrand>
            )}
          </div>

          <div className={slots.info()}>
            <h2
              className={slots.title()}
              dangerouslySetInnerHTML={{
                __html: series ? `${series} ${title}` : title
              }}
            />
            <div
              className={slots.description()}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>

          {options && options.length > 0 && (
            <UiProductOptionsBase
              options={options}
              size="s"
              onOptionClick={onOptionClick}
              maxVisible={3}
              noSelected={true}
            />
          )}
        </div>

        <div className={slots.prices()}>
          {(type === 'base' || type === 'gift' || type === 'order') && (
            <div className={slots.priceRow()}>
              {salePrice > 0 && (
                <div className={slots.salePrice()}>
                  <span>{t('discount-title')}</span>
                  <UiKitPriceBase value={salePrice} />
                </div>
              )}
              <UiKitPriceBase
                value={price}
                oldValue={oldPrice}
                classNameValue="heading-xl"
                classNameOldValue="heading-sm-bold mt-auto text-secondary-hover"
              />
            </div>
          )}

          {type === 'base' && bonusesMax > 0 && (
            <UiLoyaltyInformerCount
              bonuses={bonuses}
              bonusesMax={bonusesMax}
              isAuthorized={isAuthorized}
              showLogin={false}
              size="xs"
            />
          )}

          {type === 'base' && (count !== 0 || !onCartClick) && (
            <>
              {price > 0 ? (
                <div data-mobile-app-hidden="commerce">
                  <UiProductCounterInputBase
                    className={slots.controls()}
                    value={count}
                    onDebounceChange={onCartChange}
                    loading={isCartLoading}
                    min={1}
                    max={maxCount}
                  />
                </div>
              ) : (
                <div className={slots.countFree()}>
                  {count} {t('unit-pieces')}
                </div>
              )}
            </>
          )}

          {type === 'base' &&
            count === 0 &&
            onCartClick &&
            (maxCount ?? 0) > 0 && (
              <UiKitButtonBase
                className={slots.buy()}
                color="default"
                onClick={onCartClick}
                loading={isCartLoading}
                data-mobile-app-hidden="commerce"
              >
                {t('To cart')}
              </UiKitButtonBase>
            )}

          <div className="my-auto flex flex-col gap-4">
            {type !== 'base' && count > 0 && (
              <div className={slots.count()}>
                {count} {t('unit-pieces')}
              </div>
            )}
            {type === 'unavailable' && (
              <UiKitButtonBase
                color="secondary"
                onClick={() =>
                  utilEventProductSubscribeModal.open(mindboxId as string)
                }
              >
                {t('subscribe')}
              </UiKitButtonBase>
            )}
          </div>
        </div>

        <div className={slots.actions({ className: classNameActions })}>
          {type === 'base' &&
            price > 0 &&
            visibility !== t('Not visible individually') && (
              <button
                type="button"
                className={slots.actionItem({ isInWishlist })}
                onClick={onWishlistClick}
                data-mobile-app-hidden="commerce"
              >
                {isInWishlist ? (
                  <IconHeartActive className="size-6" />
                ) : (
                  <IconHeart className="size-6" />
                )}
              </button>
            )}
          {isCart && count > 0 && type !== 'gift' && (
            <button
              type="button"
              className={slots.actionItem()}
              onClick={onRemoveClick}
            >
              <IconDelete className="size-6" />
            </button>
          )}
        </div>
      </div>

      <GroupItems
        data={childrenItems}
        groupType={groupType}
      >
        {childrenItems?.map((item) => (
          <ProductInGroupTile
            key={item.sku}
            type={type}
            data={item}
            onWishlistClick={onWishlistChildrenClick}
          />
        ))}
      </GroupItems>
    </div>
  );
}
